/**
 * EPC Contract master cockpit — Draft/Rejected edit + submit/activate + CA handoff.
 * Reuses commercial contract API + CA particulars (clause profile / FIDIC terms).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Progress,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { ENTITY_TYPE_EPC_CONTRACT } from '@/constants/approval-entity-types';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { epcContractAdminService } from '@/services/epcContractAdmin.service';
import { formatMoney } from '@/utils/format';
import { buildContractReviewContext } from './awardReconciliationUx';
import {
  buildContractMasterReadiness,
  contractReadinessTagColor,
} from './contractMasterReadiness';
import { pursuitResolvePath } from './resolvePursuitOpportunityId';
import { CommercialModelExplainer, CommercialModelSelect } from './EpcCommercialModelGuide';
import { normalizeCommercialModel } from '@/constants/epcCommercialModels';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const CLAUSE_PROFILES = [
  { value: 'FIDIC_RED_BOOK', label: 'FIDIC Red Book' },
  { value: 'FIDIC_YELLOW_BOOK', label: 'FIDIC Yellow Book' },
  { value: 'FIDIC_SILVER_BOOK', label: 'FIDIC Silver Book' },
  { value: 'FIDIC_GREEN_BOOK', label: 'FIDIC Green Book' },
  { value: 'COMPANY_STANDARD', label: 'Company standard' },
  { value: 'CLIENT_STANDARD', label: 'Client standard' },
  { value: 'CUSTOM', label: 'Custom' },
];

export type EpcContractCockpitProps = {
  contract: Record<string, unknown>;
  onUpdated?: () => void;
};

function parseParticulars(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...(raw as Record<string, unknown>) };
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw) as unknown;
      if (p && typeof p === 'object' && !Array.isArray(p)) return { ...(p as Record<string, unknown>) };
    } catch {
      return {};
    }
  }
  return {};
}

function toDay(v: unknown): Dayjs | null {
  if (!v) return null;
  const d = dayjs(String(v));
  return d.isValid() ? d : null;
}

export const EpcContractCockpit: React.FC<EpcContractCockpitProps> = ({ contract, onUpdated }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const id = String(contract.id || '');
  const status = String(contract.status || 'Draft');
  const currency = String(contract.currency || 'AED');
  const awardId = String(contract.award_id || contract.awardId || '');
  const editable = ['Draft', 'Rejected'].includes(status);
  const canActivate = status === 'Approved';
  const isActive = status === 'Active';
  const cr = useMemo(() => buildContractReviewContext(contract), [contract]);

  const [form] = Form.useForm();
  const selectedModel = Form.useWatch('commercialModel', form) as string | undefined;

  const particularsQ = useQuery({
    queryKey: ['epc-contract-particulars', id],
    queryFn: () => epcContractAdminService.getParticulars(id),
    enabled: Boolean(id),
  });

  const particulars = useMemo(() => {
    const fromApi = (particularsQ.data as { particulars?: Record<string, unknown> } | undefined)
      ?.particulars;
    if (fromApi && typeof fromApi === 'object') return fromApi;
    return parseParticulars(contract.particulars_json || contract.particulars);
  }, [particularsQ.data, contract]);

  const clauseProfile = String(
    (particularsQ.data as { clauseProfile?: string } | undefined)?.clauseProfile ||
      contract.clause_profile ||
      contract.clauseProfile ||
      'CUSTOM'
  );

  useEffect(() => {
    form.setFieldsValue({
      title: String(contract.title || ''),
      commercialModel: normalizeCommercialModel(contract.commercial_model || contract.commercialModel) || undefined,
      contractValue: contract.contract_value != null ? Number(contract.contract_value) : undefined,
      currency,
      customerName: String(contract.customer_name || contract.customerName || ''),
      department_id: contract.department_id || contract.departmentId || undefined,
      clauseProfile,
      commencementDate: toDay(particulars.commencement_date || particulars.commencementDate),
      completionDate: toDay(
        particulars.completion_date ||
          particulars.completionDate ||
          particulars.original_completion_date
      ),
      paymentTerms: String(particulars.payment_terms || particulars.paymentTerms || ''),
      retentionPct:
        particulars.retention_pct != null || particulars.retentionPct != null
          ? Number(particulars.retention_pct ?? particulars.retentionPct)
          : undefined,
      ldDailyRate:
        particulars.ld_daily_rate != null || particulars.ldDailyRate != null
          ? Number(particulars.ld_daily_rate ?? particulars.ldDailyRate)
          : undefined,
      performanceBondPct:
        particulars.performance_bond_pct != null || particulars.performanceBondPct != null
          ? Number(particulars.performance_bond_pct ?? particulars.performanceBondPct)
          : undefined,
      advancePaymentPct:
        particulars.advance_payment_pct != null || particulars.advancePaymentPct != null
          ? Number(particulars.advance_payment_pct ?? particulars.advancePaymentPct)
          : undefined,
      scopeSummary: String(particulars.scope_summary || particulars.scopeSummary || ''),
      specialConditions: String(
        particulars.special_conditions || particulars.specialConditions || ''
      ),
    });
  }, [contract, particulars, clauseProfile, currency, form]);

  const readiness = useMemo(
    () =>
      buildContractMasterReadiness({
        contract,
        particulars,
        clauseProfile,
      }),
    [contract, particulars, clauseProfile]
  );

  const readyPct = Math.round(
    (readiness.checks.filter((c) => c.state === 'READY').length / Math.max(readiness.checks.length, 1)) *
      100
  );

  const saveM = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      await epcCommercialService.patchContract(id, {
        title: values.title,
        commercialModel: values.commercialModel,
        contractValue: values.contractValue,
        currency: values.currency || 'AED',
        customerName: values.customerName,
        departmentId: values.department_id,
      });
      const nextParticulars = {
        ...particulars,
        commencement_date: values.commencementDate
          ? dayjs(values.commencementDate as Dayjs).format('YYYY-MM-DD')
          : null,
        completion_date: values.completionDate
          ? dayjs(values.completionDate as Dayjs).format('YYYY-MM-DD')
          : null,
        original_completion_date: values.completionDate
          ? dayjs(values.completionDate as Dayjs).format('YYYY-MM-DD')
          : particulars.original_completion_date || null,
        payment_terms: values.paymentTerms || null,
        retention_pct: values.retentionPct ?? null,
        ld_daily_rate: values.ldDailyRate ?? null,
        performance_bond_pct: values.performanceBondPct ?? null,
        advance_payment_pct: values.advancePaymentPct ?? null,
        scope_summary: values.scopeSummary || null,
        special_conditions: values.specialConditions || null,
      };
      await epcContractAdminService.patchParticulars(id, {
        clauseProfile: values.clauseProfile || 'CUSTOM',
        particulars: nextParticulars,
      });
    },
    onSuccess: () => {
      message.success('Contract master saved');
      qc.invalidateQueries({ queryKey: ['epc-contracts'] });
      qc.invalidateQueries({ queryKey: ['epc-contract-particulars', id] });
      qc.invalidateQueries({ queryKey: ['epc-contract-admin-workspace', id] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  const submitM = useMutation({
    mutationFn: () => epcCommercialService.submitContract(id),
    onSuccess: () => {
      message.success('Contract submitted for approval');
      qc.invalidateQueries({ queryKey: ['epc-contracts'] });
      qc.invalidateQueries({ queryKey: ['approval-summary', ENTITY_TYPE_EPC_CONTRACT, id] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Submit failed'),
  });

  const activateM = useMutation({
    mutationFn: () => epcCommercialService.activateContract(id),
    onSuccess: () => {
      message.success('Contract activated — commercial baseline frozen');
      qc.invalidateQueries({ queryKey: ['epc-contracts'] });
      qc.invalidateQueries({ queryKey: ['epc-contract-admin-workspace', id] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Activate failed'),
  });

  const nextStep = !editable
    ? status === 'Submitted'
      ? 'Await Approval Runtime decision, then activate / open CA.'
      : status === 'Approved'
        ? 'Activate to freeze commercial baseline and unlock CA execution.'
        : isActive
          ? 'Contract is Active — administer via Contract Admin (IPC, VO, claims, EOT).'
          : undefined
    : readiness.canSubmit
      ? 'Save any remaining edits, then Submit for approval.'
      : readiness.submitBlockedReason;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Space wrap style={{ marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0 }}>
            {String(contract.contract_number || 'EPC Contract')}
          </Title>
          <Tag>{status}</Tag>
          <Tag color={contractReadinessTagColor(readiness.overall)}>{readiness.overall}</Tag>
          <Tag>{clauseProfile}</Tag>
        </Space>
        <Text type="secondary">
          {contract.contract_value != null
            ? formatMoney(Number(contract.contract_value), { currency })
            : 'No contract value'}
          {contract.customer_name || contract.customerName
            ? ` · ${contract.customer_name || contract.customerName}`
            : ''}
        </Text>
        <Progress percent={readyPct} size="small" style={{ marginTop: 8, maxWidth: 320 }} />
      </div>

      <Alert
        type={editable ? (readiness.canSubmit ? 'success' : 'warning') : 'info'}
        showIcon
        message={editable ? 'Master contract setup (Draft)' : `Status: ${status}`}
        description={
          nextStep ||
          'Complete commercial particulars used by large EPC programmes before approval.'
        }
      />

      <Card size="small" title="What to do next">
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          <li>Complete identity, commercial model, value, and employer.</li>
          <li>Set FIDIC/clause profile, dates, payment, retention, securities.</li>
          <li>Submit for Approval Runtime (`epc_contract`).</li>
          <li>After Approved → Activate (baseline freeze) → open Contract Admin.</li>
        </ol>
      </Card>

      <Card size="small" title="Master identity">
        <Form
          form={form}
          layout="vertical"
          disabled={!editable || saveM.isPending}
          onFinish={(values) => saveM.mutate(values)}
        >
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="title"
                label="Contract title"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="Project / package title" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="customerName"
                label="Customer / employer"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="Employer legal name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="department_id"
                label="Department"
                rules={[{ required: true, message: 'Department is required' }]}
              >
                <DepartmentSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="commercialModel"
                label="Commercial model"
                rules={[{ required: true, message: 'Required' }]}
              >
                <CommercialModelSelect allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="contractValue"
                label="Contract value"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="currency" label="Currency" initialValue="AED">
                <Select
                  options={['AED', 'USD', 'EUR', 'SAR', 'GBP'].map((v) => ({
                    value: v,
                    label: v,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <div style={{ marginBottom: 12 }}>
                <CommercialModelExplainer model={selectedModel} compact />
              </div>
            </Col>
          </Row>

          <Card type="inner" title="Clause profile & programme" size="small" style={{ marginBottom: 12 }}>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="clauseProfile"
                  label="Clause profile"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select options={CLAUSE_PROFILES} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="commencementDate"
                  label="Commencement"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="completionDate"
                  label="Completion"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card type="inner" title="Payment, retention & securities" size="small" style={{ marginBottom: 12 }}>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="paymentTerms"
                  label="Payment terms"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder="e.g. 30 days from certified IPC" />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item
                  name="retentionPct"
                  label="Retention %"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item name="ldDailyRate" label="LD / day">
                  <InputNumber style={{ width: '100%' }} min={0} step={1} />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item name="performanceBondPct" label="Perf. bond %">
                  <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item name="advancePaymentPct" label="Advance %">
                  <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Form.Item name="scopeSummary" label="Scope summary">
            <TextArea rows={3} placeholder="Works / packages covered by this contract" />
          </Form.Item>
          <Form.Item name="specialConditions" label="Special conditions">
            <TextArea rows={3} placeholder="Employer-specific conditions / deviations" />
          </Form.Item>

          {editable ? (
            <Button type="primary" htmlType="submit" loading={saveM.isPending}>
              Save contract master
            </Button>
          ) : (
            <Text type="secondary">Locked for edit while {status}. Reject returns to Draft-like editing.</Text>
          )}
        </Form>
      </Card>

      <Card size="small" title="Readiness">
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {readiness.checks.map((c) => (
            <div key={c.id}>
              <Tag color={contractReadinessTagColor(c.state)}>{c.state}</Tag>
              <Text>
                {c.label}
                {c.detail ? ` — ${c.detail}` : ''}
                {c.blocking ? '' : ' (advisory)'}
              </Text>
            </div>
          ))}
        </Space>
      </Card>

      <Card size="small" title="Lifecycle actions">
        <Space wrap>
          <Button
            type="primary"
            disabled={!editable || !readiness.canSubmit}
            loading={submitM.isPending}
            title={readiness.submitBlockedReason}
            onClick={() => {
              form
                .validateFields()
                .then((values) => saveM.mutateAsync(values).then(() => submitM.mutate()))
                .catch(() => message.warning('Fix validation errors before submit'));
            }}
          >
            Submit for approval
          </Button>
          <Button
            disabled={!canActivate}
            loading={activateM.isPending}
            title={!canActivate ? 'Available after Approved' : undefined}
            onClick={() => activateM.mutate()}
          >
            Activate contract
          </Button>
          <Button type="primary" ghost onClick={() => navigate(`/epc/commercial/contracts/${id}`)}>
            Open Contract Admin
          </Button>
          <Button onClick={() => navigate(`/epc/commercial/contracts/${id}/control`)}>
            Control tower
          </Button>
          <Button onClick={() => navigate('/epc/commercial/mobilizations')}>Mobilizations</Button>
          {awardId ? (
            <Button onClick={() => navigate(pursuitResolvePath('epc_award', awardId))}>
              Open pursuit
            </Button>
          ) : null}
          {cr.linked && cr.path ? (
            <Button type="link" onClick={() => navigate(cr.path!)}>
              Contract Review
            </Button>
          ) : null}
        </Space>
        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          Approval uses Approval Runtime (`epc_contract`). Activation freezes commercial baseline.
          Post-activation administration stays in Program 12.2 CA (IPC / VO / claims / EOT) — not
          duplicated here.
        </Paragraph>
      </Card>

      <Card size="small" title="Approval">
        {id ? (
          <ApprovalPanel
            entityType={ENTITY_TYPE_EPC_CONTRACT}
            entityId={id}
            extraInvalidateQueryKeys={[['epc-contracts'], ['epc-contract-admin-workspace', id]]}
          />
        ) : (
          <Alert type="warning" message="Missing contract id" />
        )}
      </Card>
    </Space>
  );
};

export default EpcContractCockpit;
